import {each, filter, find, findIndex, isEmpty, map, some} from "underscore";

export const toUiMessages = (messages) =>
  map(filter(messages, (msg) => !msg.isSummary && msg.role !== "system"), (msg) => ({
    messageId: msg.messageId,
    text: msg.displayContent || msg.content,
    sender: msg.role === "user" ? "user" : "assistant",
    createdAt: msg.createdAt,
    aborted: !!msg.aborted,
    parentMessageId: msg.parentMessageId,
    resumeRequestId: msg.resumeRequestId,
    stopProjection: msg.stopProjection,
    ...(msg.agentResult && {agentResult: msg.agentResult}),
    ...(msg.agentTaskId && {agentTaskId: msg.agentTaskId}),
    ...(msg.confirmationResult && {confirmationResult: msg.confirmationResult}),
    ...(msg.agentErrors && {agentErrors: msg.agentErrors}),
    ...(msg.generatedDocuments && {generatedDocuments: msg.generatedDocuments}),
    ...(msg.widgetType && {
      widgetType: msg.widgetType,
      widgetValue: msg.widgetValue,
      widgetLabel: msg.widgetLabel,
    }),
    ...(msg.pendingWidget && {pendingWidget: msg.pendingWidget}),
    ...(!isEmpty(msg.attachments) && {attachments: msg.attachments}),
    ...(isEmpty(msg.attachments) && msg.attachment && {attachments: [msg.attachment]}),
  }));

// Terminal stream messages are the same records used by reload, not another UI summary.
export const mergeStreamOutcomes = (previous, incoming, resumeRequestId) => {
  const messages = filter(
    previous,
    (message) =>
      !some(incoming, (saved) => saved.messageId === message.messageId) &&
      !(resumeRequestId && message.resumeRequestId === resumeRequestId),
  );
  each(incoming, (message) => {
    const parentIndex = findIndex(
      messages,
      (parent) => parent.messageId === message.parentMessageId,
    );
    messages.splice(parentIndex < 0 ? messages.length : parentIndex + 1, 0, message);
  });
  return messages;
};

const isAssistant = (message) => message.sender === "assistant" || message.role === "assistant";
const sourceKey = (message) =>
  message.resumeRequestId ?
    `resume:${message.resumeRequestId}` :
    message.parentMessageId && `input:${message.parentMessageId}`;
const isProjection = (message) => message.stopProjection || message.messageId?.startsWith("STOP#");

// Same-view refresh may show a projection before the worker saves the authoritative reply.
// Only locally stopped, source-bound messages qualify; an actual saved outcome always wins.
export const reconcileSessionMessages = (previous, incoming) => {
  const preserved = (message) =>
    find(
      previous,
      (local) =>
        local.locallyStopped && sourceKey(local) && sourceKey(local) === sourceKey(message),
    );
  const candidates = filter(
    incoming,
    (message) =>
      !isProjection(message) ||
      !some(
        incoming,
        (other) =>
          isAssistant(other) &&
          !isProjection(other) &&
          sourceKey(other) &&
          sourceKey(other) === sourceKey(message),
      ),
  );
  const messages = map(candidates, (message) =>
    isProjection(message) ? preserved(message) || message : message,
  );
  each(previous, (local) => {
    if (!local.locallyStopped || !sourceKey(local)) {
      return;
    }
    const hasOutcome = some(
      messages,
      (message) => isAssistant(message) && sourceKey(message) === sourceKey(local),
    );
    const hasQuestion =
      !!local.resumeRequestId ||
      some(messages, (message) => message.messageId === local.parentMessageId);
    if (!hasOutcome && hasQuestion) {
      const parentIndex = findIndex(
        messages,
        (message) => message.messageId === local.parentMessageId,
      );
      messages.splice(parentIndex === -1 ? messages.length : parentIndex + 1, 0, local);
    }
  });
  return messages;
};
